import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormBuilder,
    FormGroup,
    NgControl,
} from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { identifier } from '@uipath/angular/utilities';
import UiValidators from '@validators';

import {
    merge,
    Subject,
} from 'rxjs';
import {
    map,
    takeUntil,
} from 'rxjs/operators';

import { ErrorRecord } from '../queue.upsert.page';

@Component({
    selector: 'ui-time-offset-picker',
    templateUrl: './ui-time-offset-picker.component.html',
    styleUrls: ['./ui-time-offset-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: UiTimeOffsetPickerComponent,
        },
    ],
    encapsulation: ViewEncapsulation.None,
})
export class UiTimeOffsetPickerComponent implements
    ControlValueAccessor,
    OnInit,
    OnDestroy,
    MatFormFieldControl<number> {

    @Input()
    public get value(): number {
        return this._value;
    }
    public set value(state: number) {
        this._value = state;
        this.stateChanges.next();
    }

    @Input()
    get required() {
        return this._required;
    }
    set required(req) {
        this._required = req;
        this.stateChanges.next();
    }

    @Input()
    disabled = false;

    @Input()
    public daysPlaceholder = 'Days';

    @Input()
    public hoursPlaceholder = 'Hours';

    @Input()
    public daysRange = { min: 0, max: 90 };

    @Input()
    public hoursRange = { min: 0, max: 23 };

    /**
     * Custom error list that component will validate against
     *
     * @type {ErrorRecord[]}
     * @memberof UiTimeOffsetPickerComponent
     */
    @Input()
    public errorsToCheck: ErrorRecord[] = [];
    /**
     * Error that is displayed in view, must be an entry in `errorsToCheck`
     *
     * @type {ErrorRecord}
     * @memberof UiTimeOffsetPickerComponent
     */
    public errorToDisplay?: ErrorRecord;
    public form!: FormGroup;

    public id: string;
    public stateChanges = new Subject<void>();
    public placeholder = '';
    public labelText = '';
    public focused = false;
    public empty = false;
    public shouldLabelFloat = false;
    public describedBy = '';
    public errorState = false;

    public daysErrorStateMatcher = {
        isErrorState: (): boolean => {
            return this.form.get('days')!.hasError('range') ? true : this._hasError();
        },
    };

    public hoursErrorStateMatcher = {
        isErrorState: (): boolean => {
            return this.form.get('hours')!.hasError('range') ? true : this._hasError();
        },
    };

    private _value = 0;
    private _required = false;
    private _randomName: string;
    private _destroyed$ = new Subject();

    constructor(
        public ngControl: NgControl,
        private _cd: ChangeDetectorRef,
        private _fb: FormBuilder,
    ) {
        this.ngControl.valueAccessor = this;
        this._randomName = identifier();
        this.id = 'ui-time-offset-picker-' + this._randomName;
    }

    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join(' ');
    }

    onContainerClick(_event: MouseEvent) { }

    ngOnInit() {
        this.form = this._fb.group({
            days: [this._days, UiValidators.range(this.daysRange.min, this.daysRange.max)],
            hours: [this._hours, UiValidators.range(this.hoursRange.min, this.hoursRange.max)],
        });

        merge(
            this.form.get('days')!.valueChanges,
            this.form.get('hours')!.valueChanges,
        ).pipe(
            map(_ => this.form.get('days')!.value * 1440 + this.form.get('hours')!.value * 60),
            takeUntil(this._destroyed$),
        ).subscribe(value => {
            this.registerChange(value);
            this.value = value;

            this._setErrors();
            this._cd.markForCheck();
        });
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this.stateChanges.complete();
    }

    public registerChange: (_: any) => void = () => { };
    public registerTouch: () => void = () => { };

    public writeValue(minutes: number) {
        this.value = minutes;

        if (this.form) {
            this.form.get('days')!.setValue(this._days, { emitEvent: false });
            this.form.get('hours')!.setValue(this._hours, { emitEvent: false });
        }

        this._setErrors();
        this._cd.markForCheck();
    }

    public registerOnChange(fn: (_: any) => void): void {
        this.registerChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.registerTouch = fn;
    }

    private get _days() {
        return Math.floor(this._value / 1440);
    }

    private get _hours() {
        return Math.floor((this._value % 1440) / 60);
    }

    private _hasError() {
        this.errorToDisplay = this.errorsToCheck
            .find(({ error }) => this.ngControl.hasError(error));

        return !!this.errorToDisplay;
    }

    private _setErrors() {
        if (
            this.ngControl &&
            this.form &&
            (this.form.get('days')!.errors || this.form.get('hours')!.errors)
        ) {
            this.ngControl.control!.setErrors({ invalid: true });
            this.stateChanges.next();
        }
    }
}
